import React from 'react'
import { PiBuildings, PiEnvelope, PiHourglass } from 'react-icons/pi'
import { EnrichedActionCard } from './EnrichedActionCard'
import type {
  EnrichedAction,
  SignalType,
  ActionTypeEnum,
  SentimentType,
} from '../../services/action_summary_api'
import { cn } from '../UI/cn'
import { SIGNAL_ICON_COLORS, formatSignalLabel } from './constants'

interface CompanySectionProps {
  /**
   * Company name
   */
  companyName: string

  /**
   * Company ID (for keying)
   */
  companyId: number

  /**
   * All actions for this company
   */
  actions: EnrichedAction[]

  /**
   * Whether section is expanded
   */
  isExpanded: boolean

  /**
   * Toggle expansion callback
   */
  onToggle: () => void

  /**
   * Callback when an action card is clicked
   */
  onActionClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum
  ) => void

  /**
   * Callback when user provides sentiment feedback (thumbs up/down)
   */
  onSentimentClick?: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum,
    sentiment: SentimentType
  ) => void

  /**
   * Function to get existing sentiment for an action (for persistence)
   */
  getSentimentForAction?: (action: EnrichedAction) => SentimentType | null
}

/**
 * Signal type icons mapping.
 * Uses shared color constants for consistency with other components.
 */
const SIGNAL_ICONS: Record<
  SignalType,
  React.ComponentType<{ size: number; className: string }>
> = {
  unreplied_email: PiEnvelope,
  inactive_deal: PiHourglass,
}

/**
 * Renders the appropriate icon for a signal type with consistent styling.
 */
const SignalIcon: React.FC<{ signalType: SignalType; size?: number }> = ({
  signalType,
  size = 12,
}) => {
  const IconComponent = SIGNAL_ICONS[signalType]
  if (!IconComponent) return null
  return (
    <IconComponent size={size} className={SIGNAL_ICON_COLORS[signalType]} />
  )
}

/**
 * Displays all actions for a single company.
 *
 * Shows company name with count, and all related actions underneath.
 * Allows users to focus on one relationship at a time.
 */
export const CompanySection: React.FC<CompanySectionProps> = ({
  companyName,
  companyId,
  actions,
  isExpanded,
  onToggle,
  onActionClick,
  onSentimentClick,
  getSentimentForAction,
}) => {
  // Get subcategory from first action (all actions for same company share subcategory)
  const companySubcategory = actions[0]?.company_subcategory

  // Count actions by signal type
  const signalCounts = actions.reduce(
    (acc, action) => {
      acc[action.signal_type] = (acc[action.signal_type] || 0) + 1
      return acc
    },
    {} as Record<SignalType, number>
  )

  const handleCardClick = (action: EnrichedAction) => {
    if (onActionClick) {
      onActionClick(action, action.signal_type, action.action_type)
    }
  }

  const handleSentimentClick = (
    action: EnrichedAction,
    sentiment: SentimentType
  ) => {
    if (onSentimentClick) {
      onSentimentClick(
        action,
        action.signal_type,
        action.action_type,
        sentiment
      )
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Company Header - Always visible, clickable to expand/collapse */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-fb-green-50 text-fb-dark-green">
            <PiBuildings size={18} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 text-sm">
              {companyName}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {companySubcategory && (
                <>
                  <span className="text-xs text-gray-500">
                    {companySubcategory}
                  </span>
                  <span className="text-gray-300">·</span>
                </>
              )}
              {/* Signal type badges */}
              {Object.entries(signalCounts).map(([type, count]) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 text-xs text-gray-500"
                >
                  <SignalIcon signalType={type as SignalType} />
                  <span>{count}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {actions.length} {actions.length === 1 ? 'action' : 'actions'}
          </span>
          <svg
            className={cn(
              'w-5 h-5 text-gray-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Expanded content - Action cards */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
          {actions.map((action, index) => (
            <div
              key={`${companyId}-${action.deal_id}-${index}`}
              className="relative"
            >
              {/* Signal type indicator on left */}
              <div className="absolute -left-2 top-3 flex items-center gap-1 text-xs text-gray-400">
                <SignalIcon signalType={action.signal_type} />
              </div>
              <div className="ml-2">
                <EnrichedActionCard
                  action={action}
                  onCardClick={() => handleCardClick(action)}
                  onSentimentClick={(sentiment) =>
                    handleSentimentClick(action, sentiment)
                  }
                  initialSentiment={getSentimentForAction?.(action) ?? null}
                />
                {/* Quick context about why this action */}
                <div className="mt-1 ml-1 text-xs text-gray-400">
                  {formatSignalLabel(
                    action.signal_type,
                    action.days_unreplied,
                    action.days_stale,
                    action.days_in_stage,
                    action.current_stage
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
